import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Flex, Typography } from 'antd'
import { Building2, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsJoinRequestsCreateMutation,
  OrganizationJoinRequestStatusEnum,
  type DomainMatchEntry,
  type OrganizationJoinRequest
} from 'src/api/main'
import { AvatarWithInitials } from 'src/components'
import { useShowApiErrors } from 'src/utils'

import { useStyles } from '../styles'

import { StepHeader } from './StepHeader'

const { Text } = Typography

type Props = {
  /** Workspaces on this person's email domain that they are not already in. */
  entries: DomainMatchEntry[]
  /** Leaves the fork and starts the ordinary create-a-workspace flow. */
  onCreateInstead: () => void
}

/**
 * The fork a signup lands on when their email domain already belongs to one or
 * more workspaces: ask to be let into one, rather than standing up another
 * beside it.
 *
 * Asking grants nothing — the platform answers 202 and notifies that
 * workspace's administrators, who decide. Each row carries its own verdict,
 * because the platform reports a request per workspace and somebody may well
 * have asked one company and not the other.
 *
 * Creating a workspace anyway stays available, quietly. The domain match is a
 * strong hint, not an entitlement: a contractor, a second brand, or a team
 * whose administrator has left all need a way past a screen that would
 * otherwise be a dead end with no one on the other side of it.
 */
export const JoinExistingStep = ({ entries, onCreateInstead }: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const showApiErrors = useShowApiErrors()

  /** Requests sent during this visit, keyed by workspace. */
  const [sent, setSent] = useState<Record<number, OrganizationJoinRequest>>({})
  const [sending, setSending] = useState<number | null>(null)

  const { mutateAsync: askToJoin } = useMutation(
    organizationsJoinRequestsCreateMutation()
  )

  const send = async (organizationId: number) => {
    setSending(organizationId)
    try {
      const request = await askToJoin({
        path: { organization_id: organizationId }
      })
      setSent((current) => ({ ...current, [organizationId]: request }))
    } catch (error) {
      showApiErrors(error)
    } finally {
      setSending(null)
    }
  }

  const requestFor = (entry: DomainMatchEntry) =>
    sent[entry.id] ?? entry.request

  const hasPending = entries.some(
    (entry) =>
      requestFor(entry)?.status === OrganizationJoinRequestStatusEnum.PENDING
  )

  return (
    <Flex vertical gap={16} flex={1}>
      <StepHeader
        title={t('workspaces.onboarding.join.title')}
        subtitle={t('workspaces.onboarding.join.subtitle')}
      />

      <div className={styles.joinList}>
        {entries.map((entry) => {
          const request = requestFor(entry)
          const pending =
            request?.status === OrganizationJoinRequestStatusEnum.PENDING
          const rejected =
            request?.status === OrganizationJoinRequestStatusEnum.REJECTED

          return (
            <div key={entry.id} className={styles.joinRow}>
              <AvatarWithInitials name={entry.name} shape="square" size={36} />

              <Flex vertical flex={1} style={{ minWidth: 0 }}>
                <Text strong className={styles.joinName}>
                  {entry.name}
                </Text>
                <Text type="secondary">
                  <Flex align="center" gap={6}>
                    <Building2 size={13} />
                    {t('workspaces.onboarding.join.existingWorkspace')}
                  </Flex>
                </Text>
              </Flex>

              {rejected ? (
                <Text type="secondary">
                  {t('workspaces.onboarding.join.rejectedTitle')}
                </Text>
              ) : null}

              <Button
                type={pending ? 'text' : 'primary'}
                icon={pending ? undefined : <Send size={14} />}
                iconPlacement="end"
                loading={sending === entry.id}
                disabled={pending}
                onClick={() => void send(entry.id)}
              >
                {pending
                  ? t('workspaces.onboarding.join.requested')
                  : t('workspaces.onboarding.join.request')}
              </Button>
            </div>
          )
        })}
      </div>

      {hasPending ? (
        <Alert
          type="info"
          showIcon
          message={t('workspaces.onboarding.join.pendingTitle')}
          description={t('workspaces.onboarding.join.pendingBody')}
        />
      ) : null}

      <Button block type="text" size="large" onClick={onCreateInstead}>
        {t('workspaces.onboarding.join.createInstead')}
      </Button>
    </Flex>
  )
}
